import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { searchContracts } from '../../structured-data/structuredDataApi';

// Search-as-you-type contract picker (shared by TestRunPane and the AI draft
// pane). The picked contract shows as a chip with an X to clear.
export default function ContractPicker({ picked, onPick, onClear, ariaLabel = 'Search contracts' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  // Viewport coords for the results menu. Cards have overflow:clip, which
  // cut the dropdown off when the pane sits near the card's bottom edge —
  // position:fixed with viewport coords escapes that (same approach as
  // components/Combobox.js). Flips up when there's more room above.
  const [menuPos, setMenuPos] = useState(null);
  const debounce = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!q.trim()) { setResults([]); setOpen(false); return undefined; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const res = await searchContracts(q.trim());
        setResults(res.contracts || []);
        setOpen(true);
      } catch (_) {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(debounce.current);
  }, [q]);

  const updateMenuPos = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const gap = 4;
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    const maxHeight = 260;
    const openUp = below < maxHeight && above > below;
    setMenuPos({
      left: rect.left,
      top: openUp ? rect.top - gap : rect.bottom + gap,
      width: rect.width,
      maxHeight: Math.min(maxHeight, Math.max(120, (openUp ? above : below) - gap - 8)),
      openUp,
    });
  }, []);

  // While open: keep the menu anchored to the input on scroll/resize and
  // close on any pointer-down outside the input or the menu.
  useEffect(() => {
    if (!open) return undefined;
    updateMenuPos();
    const onDown = (e) => {
      const inInput = inputRef.current && inputRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inInput && !inMenu) setOpen(false);
    };
    const onScrollOrResize = () => updateMenuPos();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updateMenuPos]);

  if (picked) {
    return (
      <span className="chip neutral" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {picked.title}
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear picked contract"
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', display: 'inline-flex', color: 'inherit' }}
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  return (
    <div style={{ position: 'relative', maxWidth: 420 }}>
      <input
        ref={inputRef}
        className="input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (results.length > 0 && q.trim()) setOpen(true); }}
        placeholder="Search contracts…"
        aria-label={ariaLabel}
      />
      {open && results.length > 0 && menuPos && (
        <div
          ref={menuRef}
          className="card"
          style={{
            position: 'fixed',
            top: menuPos.openUp ? 'auto' : menuPos.top,
            bottom: menuPos.openUp ? (window.innerHeight - menuPos.top) : 'auto',
            left: menuPos.left,
            width: menuPos.width,
            zIndex: 1000,
            maxHeight: menuPos.maxHeight,
            overflowY: 'auto',
            padding: 4,
          }}
        >
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => { onPick(c); setQ(''); setOpen(false); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                background: 'none', border: 0, cursor: 'pointer', borderRadius: 6,
                fontFamily: 'inherit', fontSize: 13, color: 'var(--text-primary)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
            >
              {c.title}
              <span className="muted" style={{ display: 'block', fontSize: 11.5 }}>
                {[c.counterparty, c.contractType].filter(Boolean).join(' · ')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
